import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";

const inputCls = "bg-white/5 border border-white/25 px-2 py-1.5 text-sm focus:border-primary outline-none transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none";
const btnCls = "label-mono px-4 py-2 bg-primary text-black hover:bg-[#E6C766] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50";

const NumField = ({ label, value, onChange, testid, prefix }) => (
  <div>
    <label className="label-mono text-muted-foreground text-[0.55rem] block mb-1">{label}</label>
    <div className="flex items-center gap-1">
      {prefix && <span className="text-muted-foreground text-sm">{prefix}</span>}
      <input data-testid={testid} type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} w-24`} />
    </div>
  </div>
);

export const ShippingRatesEditor = () => {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/shipping-rates").then(({ data }) => setR(data)).catch(() => {}); }, []);
  if (!r) return null;
  const set = (k, v) => setR((x) => ({ ...x, [k]: v }));
  const setTier = (i, k, v) => setR((x) => ({ ...x, tiers: x.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const save = async () => {
    setSaving(true);
    try {
      const n = (v) => parseFloat(v);
      const { data } = await api.put("/admin/settings/shipping-rates", {
        tiers: r.tiers.map((t) => ({ max_oz: n(t.max_oz), price: n(t.price) })),
        over_price: n(r.over_price), express_addon: n(r.express_addon),
        default_weight_oz: n(r.default_weight_oz), free_threshold: n(r.free_threshold),
      });
      setR(data); toast.success("Shipping rates saved");
    } catch { toast.error("Check the rates — every field needs a number (weights above 0)"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="shipping-rates-editor" className="border border-black/10 bg-surface p-6">
      <span className="label-mono text-primary block mb-1">Shipping Rates by Weight</span>
      <p className="text-xs text-muted-foreground mb-5">Standard price is picked from the first tier the cart weight fits in. Express = that tier price + the Express add-on.</p>
      <div className="space-y-2 mb-5">
        {r.tiers.map((t, i) => (
          <div key={i} data-testid={`tier-row-${i}`} className="flex items-center gap-2 text-sm flex-wrap">
            <span className="text-muted-foreground w-16">Up to</span>
            <input data-testid={`tier-max-${i}`} type="number" min="0" step="0.1" value={t.max_oz} onChange={(e) => setTier(i, "max_oz", e.target.value)} className={`${inputCls} w-20`} />
            <span className="text-muted-foreground">oz =</span>
            <span className="text-muted-foreground">$</span>
            <input data-testid={`tier-price-${i}`} type="number" min="0" step="0.01" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} className={`${inputCls} w-20`} />
            <button data-testid={`tier-remove-${i}`} disabled={r.tiers.length === 1} onClick={() => set("tiers", r.tiers.filter((_, j) => j !== i))} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-30"><Trash size={14} /></button>
          </div>
        ))}
        <button data-testid="tier-add" onClick={() => set("tiers", [...r.tiers, { max_oz: "", price: "" }])} className="label-mono text-[0.6rem] text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"><Plus size={12} /> Add tier</button>
      </div>
      <div className="flex flex-wrap gap-5 items-end">
        <NumField label="Over last tier" prefix="$" value={r.over_price} onChange={(v) => set("over_price", v)} testid="rate-over-price" />
        <NumField label="Express add-on" prefix="+$" value={r.express_addon} onChange={(v) => set("express_addon", v)} testid="rate-express-addon" />
        <NumField label="Free standard over" prefix="$" value={r.free_threshold} onChange={(v) => set("free_threshold", v)} testid="rate-free-threshold" />
        <NumField label="No-weight product counts as (oz)" value={r.default_weight_oz} onChange={(v) => set("default_weight_oz", v)} testid="rate-default-weight" />
        <button data-testid="save-shipping-rates" onClick={save} disabled={saving} className={btnCls}><FloppyDisk size={14} /> {saving ? "Saving…" : "Save rates"}</button>
      </div>
    </div>
  );
};

export const WeightsTable = ({ products, onSaved }) => {
  const [w, setW] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setW(Object.fromEntries(products.map((p) => [p.id, p.weight_oz ?? ""]))); }, [products]);
  const changed = products.filter((p) => String(w[p.id] ?? "") !== String(p.weight_oz ?? ""));
  const missing = products.filter((p) => w[p.id] === "" || w[p.id] == null).length;
  const save = async () => {
    const bad = changed.find((p) => w[p.id] !== "" && !(parseFloat(w[p.id]) > 0));
    if (bad) { toast.error(`${bad.name}: weight must be greater than 0`); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/products/weights", { weights: Object.fromEntries(changed.map((p) => [p.id, w[p.id] === "" ? null : parseFloat(w[p.id])])) });
      toast.success(`Saved weights for ${data.updated} product${data.updated === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save weights"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="weights-table" className="border border-black/10 bg-surface">
      <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-b border-black/10">
        <div>
          <span className="label-mono text-primary block">Product Weights (oz)</span>
          <span data-testid="weights-missing" className="text-xs text-muted-foreground">{missing ? `${missing} product${missing === 1 ? "" : "s"} without a weight` : "All products have a weight"}</span>
        </div>
        <button data-testid="save-all-weights" onClick={save} disabled={saving || changed.length === 0} className={btnCls}>
          <FloppyDisk size={14} /> {saving ? "Saving…" : `Save all${changed.length ? ` (${changed.length})` : ""}`}
        </button>
      </div>
      <div className="divide-y divide-black/10">
        {products.map((p) => (
          <div key={p.id} data-testid={`weight-row-${p.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
            <span className="text-sm truncate">{p.name}</span>
            <div className="flex items-center gap-1.5 shrink-0">
              <input data-testid={`weight-input-${p.id}`} type="number" min="0" step="0.01" placeholder="—" value={w[p.id] ?? ""}
                onChange={(e) => setW((x) => ({ ...x, [p.id]: e.target.value }))}
                className={`${inputCls} w-20 text-right ${String(w[p.id] ?? "") !== String(p.weight_oz ?? "") ? "border-primary" : ""}`} />
              <span className="label-mono text-muted-foreground text-[0.6rem]">oz</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
